// GPX 1.1 形式のエクスポート / インポート
// 業界標準フォーマット。他アプリ(YAMAP / ヤマレコ等)との相互運用を保証
//
// 仕様: https://www.topografix.com/GPX/1/1/

import { escapeHtml } from './ui.js';

// XML 用エスケープ(escapeHtml は HTML 用なので & < > のみで十分だが、" も含めて安全に)
function xmlEscape(s) {
  if (s === null || s === undefined) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// track: { name, points: [{lat, lng, ele, ts}], stats, startTs, endTs }
export function exportGpx(track) {
  const points = track.points || [];
  const trkpts = points
    .map((p) => {
      const lat = Number(p.lat).toFixed(6);
      const lon = Number(p.lng).toFixed(6);
      const ele = typeof p.ele === 'number' ? `<ele>${p.ele.toFixed(1)}</ele>` : '';
      const ts = p.ts ? `<time>${new Date(p.ts).toISOString()}</time>` : '';
      return `      <trkpt lat="${lat}" lon="${lon}">${ele}${ts}</trkpt>`;
    })
    .join('\n');

  const metaTime = track.startTs
    ? `  <metadata><time>${new Date(track.startTs).toISOString()}</time></metadata>`
    : '';

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Sentier"
  xmlns="http://www.topografix.com/GPX/1/1"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
${metaTime}
  <trk>
    <name>${xmlEscape(track.name || '無題の登山')}</name>
    <trkseg>
${trkpts}
    </trkseg>
  </trk>
</gpx>
`;
}

// GPX 文字列をパースして {name, points, startTs, endTs} を返す
// DOMParser を使う簡易実装。エラー時は throw
//
// セキュリティ: XML 外部実体参照(XXE)は DOMParser ではデフォルトで無効
// 加えて、巨大ファイルは事前にバイトサイズで弾く
export function parseGpx(text) {
  if (typeof text !== 'string') throw new Error('GPX 文字列が必要です');
  if (text.length > 50 * 1024 * 1024) throw new Error('GPX が大きすぎます(50MB 超)');

  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'application/xml');
  const parseError = doc.querySelector('parsererror');
  if (parseError) throw new Error('GPX のパースに失敗しました');

  const trk = doc.querySelector('trk');
  if (!trk) throw new Error('trk 要素が見つかりません');

  const nameEl = trk.querySelector('name');
  const name = nameEl ? nameEl.textContent.trim() : '';

  const trkpts = Array.from(trk.querySelectorAll('trkpt'));
  const points = trkpts
    .map((pt) => {
      const lat = parseFloat(pt.getAttribute('lat'));
      const lng = parseFloat(pt.getAttribute('lon'));
      const eleEl = pt.querySelector('ele');
      const timeEl = pt.querySelector('time');
      const ele = eleEl ? parseFloat(eleEl.textContent) : null;
      const ts = timeEl ? Date.parse(timeEl.textContent) : null;
      // S3-2: 緯度経度の範囲バリデーション
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
      return { lat, lng, ele, ts };
    })
    .filter(Boolean);

  if (points.length === 0) throw new Error('有効な軌跡点がありません');

  const startTs = points[0].ts || Date.now();
  const endTs = points[points.length - 1].ts || Date.now();

  return { name, points, startTs, endTs };
}

// Blob をダウンロードトリガー
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 200);
}
